import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import PaymentIframe, {
  PelecardResponse,
} from '../../../../../pages/user-page/pages/i-fram-asd';
import { PaymentResponse } from '../../../../../api/types/payments/gateway/payment-gateway-response';
import { getPaymentLinkOperations } from '../../../../../pages/user-page/pages/payment-services/operations/get-payment-link-operations';
import { onPaymentSuccessOperation } from '../../../../../pages/user-page/pages/payment-services/operations/on-payment-success-operation';
import { UserPaymentServiceWithPriceList } from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

interface CreditCardPaymentDialogProps {
  visible: boolean;
  onHide: () => void;
  selectedService: UserPaymentServiceWithPriceList | null;
  userId: number;
}

export const CreditCardPaymentDialog: React.FC<
  CreditCardPaymentDialogProps
> = ({ visible, onHide, selectedService, userId }) => {
  const [showAmountDialog, setShowAmountDialog] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentUrl, setPaymentUrl] = useState<
    (PaymentResponse & { allocationId: number }) | null
  >(null);
  const [loadingNotifySuccess, setLoadingNotifySuccess] = useState(false);

  // Initialize payment amount when dialog opens
  React.useEffect(() => {
    if (visible && selectedService) {
      const totalPaymentAmount =
        selectedService.allocations.userPayments?.reduce(
          (sum, payment) => sum + payment.paymentAmount,
          0,
        ) ?? 0;
      const remainingAmount =
        selectedService.currentPrices.price - totalPaymentAmount;
      setPaymentAmount(remainingAmount);
      setShowAmountDialog(true);
      setPaymentUrl(null);
      setLoadingNotifySuccess(false);
    }
  }, [visible, selectedService]);

  const handleConfirmAmount = async () => {
    if (!selectedService || !paymentAmount || paymentAmount <= 0) {
      return;
    }

    setShowAmountDialog(false);

    // Generate payment link
    await getPaymentLinkOperations(
      selectedService.allocations.allocationEntity.id,
      userId,
      paymentAmount,
      setPaymentUrl,
    );
  };

  const handleOnSuccessPayment = async (data: PelecardResponse) => {
    await onPaymentSuccessOperation(
      paymentUrl?.allocationId,
      userId,
      paymentUrl?.pendingPaymentId,
      setLoadingNotifySuccess,
      setPaymentUrl,
    );
  };

  const resetLink = () => {
    setPaymentUrl(null);
    setShowAmountDialog(true);
  };

  const handleDialogHide = () => {
    setPaymentUrl(null);
    setShowAmountDialog(true);
    setLoadingNotifySuccess(false);
    onHide();
  };

  const maxAmount = selectedService
    ? selectedService.currentPrices.price -
      (selectedService.allocations.userPayments?.reduce(
        (sum, payment) => sum + payment.paymentAmount,
        0,
      ) ?? 0)
    : 0;

  return (
    <>
      {/* Amount Input Dialog */}
      <Dialog
        header="בחר סכום לתשלום"
        visible={visible && showAmountDialog && !paymentUrl}
        onHide={handleDialogHide}
        style={{ width: '400px' }}
        footer={
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}
          >
            <Button
              label="ביטול"
              icon="pi pi-times"
              onClick={handleDialogHide}
              className="p-button-text"
            />
            <Button
              label="המשך לתשלום"
              icon="pi pi-credit-card"
              onClick={handleConfirmAmount}
              disabled={!paymentAmount || paymentAmount <= 0}
            />
          </div>
        }
      >
        <div style={{ padding: '1rem', textAlign: 'right' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
            }}
          >
            סכום לתשלום:
          </label>
          <InputNumber
            value={paymentAmount}
            onValueChange={(e) => setPaymentAmount(e.value || 0)}
            mode="currency"
            currency="ILS"
            locale="he-IL"
            max={maxAmount}
            min={1}
            style={{ width: '100%', direction: 'ltr' }}
          />
        </div>
      </Dialog>

      {/* Payment Dialog */}
      {!!paymentUrl && (
        <Dialog
          header="תשלום"
          visible={paymentUrl !== null}
          onHide={resetLink}
          style={{
            width: '700px',
            height: '90vh',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          {loadingNotifySuccess ? (
            <ProgressSpinner
              style={{
                width: '400px',
                height: '400px',
                display: 'flex',
                justifyContent: 'center',
              }}
              strokeWidth="3"
              fill="var(--surface-ground)"
              animationDuration=".5s"
            />
          ) : (
            <PaymentIframe
              src={paymentUrl!.url}
              successLink={paymentUrl!.successUrl}
              failLink={paymentUrl!.failUrl}
              onPaymentSuccess={(data: PelecardResponse) => {
                handleOnSuccessPayment(data);
              }}
              onPaymentError={(data?: PelecardResponse) => {
                //
              }}
            />
          )}
        </Dialog>
      )}
    </>
  );
};
